import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, FileText, Shield, ClipboardCheck } from 'lucide-react';

const QualityCompliance = () => {
  const qaApproach = [
    'Sampling plan designed for each commodity type',
    'COA (Certificate of Analysis) capture and verification',
    'Specification checks against buyer requirements',
    'Corrective action tracking and resolution',
    'Lot performance scorecards for continuous improvement',
    'Quality gate approvals before proceeding to next stage'
  ];

  const documentationItems = [
    'Commercial invoice templates',
    'Packing list and specifications',
    'Certificate of Origin (as required)',
    'Health/Phytosanitary certificates',
    'Quality certificates and COAs',
    'Fumigation certificates (where applicable)',
    'Customs documentation by destination',
    'Buyer-specific documentation requirements'
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Quality & Compliance Approach</h1>
            <p className="text-xl text-slate-700">
              We standardize quality checkpoints and documentation readiness to reduce delays and disputes—especially important in food commodity supply chains.
            </p>
          </div>
        </div>
      </section>

      {/* QA Approach */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://images.pexels.com/photos/8940363/pexels-photo-8940363.jpeg?w=800"
                  alt="Quality control testing"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
              <div>
                <div className="flex items-center mb-6">
                  <CheckCircle2 className="w-12 h-12 text-blue-600 mr-4" />
                  <h2 className="text-4xl font-bold">QA Approach</h2>
                </div>
                <p className="text-lg text-slate-700 mb-6">
                  Our quality assurance methodology ensures consistency across lots through systematic checkpoints and documentation.
                </p>
                <ul className="space-y-3">
                  {qaApproach.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-blue-600 mr-2 font-bold">✓</span>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Export Documentation */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="md:order-2">
                <img
                  src="https://images.unsplash.com/photo-1635859890085-ec8cb5466806?w=800"
                  alt="Export documentation and paperwork"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
              <div className="md:order-1">
                <div className="flex items-center mb-6">
                  <FileText className="w-12 h-12 text-blue-600 mr-4" />
                  <h2 className="text-4xl font-bold">Export Documentation</h2>
                </div>
                <p className="text-lg text-slate-700 mb-6">
                  Documentation requirements vary by product and destination. We provide a pre-shipment checklist and completeness review for every order.
                </p>
                <Card className="p-6 bg-white">
                  <h3 className="text-xl font-semibold mb-4">Standard Documentation</h3>
                  <ul className="space-y-2">
                    {documentationItems.map((item, index) => (
                      <li key={index} className="flex items-start text-sm">
                        <span className="text-blue-600 mr-2">•</span>
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Standards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Compliance Standards</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="p-8 text-center hover:shadow-xl transition-shadow">
                <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Food Safety</h3>
                <p className="text-slate-600">
                  Compliance with international food safety standards and destination-specific regulations
                </p>
              </Card>
              <Card className="p-8 text-center hover:shadow-xl transition-shadow">
                <ClipboardCheck className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Quality Standards</h3>
                <p className="text-slate-600">
                  Adherence to buyer specifications and industry quality benchmarks for each commodity
                </p>
              </Card>
              <Card className="p-8 text-center hover:shadow-xl transition-shadow">
                <FileText className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Export Regulations</h3>
                <p className="text-slate-600">
                  Compliance with origin and destination export/import regulations and customs requirements
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Traceability */}
      <section className="py-20 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Batch-Level Traceability</h2>
            <p className="text-lg text-slate-700 mb-8">
              Every shipment has a complete traceability dossier from origin to dispatch, providing buyers with confidence and audit-ready documentation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 text-left">
                <h3 className="font-semibold mb-3">Origin Tracking</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Source identification</li>
                  <li>• Batch/lot numbering system</li>
                  <li>• Harvest/production dates</li>
                  <li>• Initial quality documentation</li>
                </ul>
              </Card>
              <Card className="p-6 text-left">
                <h3 className="font-semibold mb-3">Process Tracking</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Processing facility records</li>
                  <li>• Quality gate checkpoints</li>
                  <li>• Storage conditions monitoring</li>
                  <li>• Chain of custody documentation</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Quality You Can Trust</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our systematic approach to quality and documentation reduces disputes and ensures smooth customs clearance.
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Discuss Your Requirements</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/eximstack">Learn About EximStack</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QualityCompliance;
